import React, { useCallback, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRight,
  ChevronDown,
  ArrowLeft,
  User,
  Briefcase,
  UserCircle2,
  Handshake,
  GraduationCap,
  TrendingUp,
  Trophy,
  MessageSquare,
  Phone,
  Mail,
  ShieldCheck,
  Check,
} from 'lucide-react';
import FranchiseHeader from '../components/FranchiseHeader';
import FranchiseApplyFooter from '../components/FranchiseApplyFooter';
import Turnstile, { HoneypotField, HONEYPOT_FIELD } from '../components/Turnstile';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/* Page-scoped neon-green accent (kept separate from the rest of the site so
   only this page uses the brighter #39FF14 accent requested in the spec.) */
const FA_GREEN = '#39FF14';

const BENEFITS = [
  { t: 'Proven Business Model',      icon: Handshake,      body: 'A step-by-step system built on 30+ years of success.' },
  { t: 'Training & Support',         icon: GraduationCap,  body: 'Comprehensive training and ongoing coaching every step of the way.' },
  { t: 'Scalable Opportunity',       icon: TrendingUp,     body: 'Multiple revenue streams and territories designed for growth.' },
  { t: 'Strong Brand Recognition',   icon: Trophy,         body: 'A trusted name with a reputation that opens doors.' },
];

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
];

const HEAR_ABOUT = [
  'Google Search',
  'Social Media',
  'Referral from a Friend',
  'Existing Ionic Client',
  'Franchise Directory',
  'News Article / Podcast',
  'Other',
];

/* ---------- Field atoms (page-scoped, neon-green outlines) ---------- */
const Label = ({ children, htmlFor }) => (
  <label htmlFor={htmlFor} className="block text-white text-[13.5px] font-medium mb-1.5">
    {children}
  </label>
);

const fieldClass =
  'w-full bg-transparent rounded-md px-4 py-3 text-white text-[14px] placeholder:text-white/40 outline-none transition-colors';
const fieldStyle = {
  border: `1px solid ${FA_GREEN}66`,
};
const fieldFocusHandlers = {
  onFocus: (e) => { e.currentTarget.style.borderColor = FA_GREEN; e.currentTarget.style.boxShadow = `0 0 0 3px ${FA_GREEN}22`; },
  onBlur:  (e) => { e.currentTarget.style.borderColor = `${FA_GREEN}66`; e.currentTarget.style.boxShadow = 'none'; },
};

const Input = ({ id, ...rest }) => (
  <input id={id} data-testid={`field-${id}`} className={fieldClass} style={fieldStyle} {...fieldFocusHandlers} {...rest} />
);

const Textarea = ({ id, ...rest }) => (
  <textarea
    id={id}
    data-testid={`field-${id}`}
    rows={3}
    className={`${fieldClass} resize-y min-h-[96px]`}
    style={fieldStyle}
    {...fieldFocusHandlers}
    {...rest}
  />
);

const Select = ({ id, children, ...rest }) => (
  <div className="relative">
    <select
      id={id}
      data-testid={`field-${id}`}
      className={`${fieldClass} appearance-none pr-9 cursor-pointer`}
      style={fieldStyle}
      {...fieldFocusHandlers}
      {...rest}
    >
      {children}
    </select>
    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: FA_GREEN }} />
  </div>
);

const SectionTitle = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2.5 mb-4 mt-8 first:mt-0">
    <Icon className="w-5 h-5" style={{ color: FA_GREEN, filter: `drop-shadow(0 0 8px ${FA_GREEN}88)` }} strokeWidth={1.75} />
    <h3 className="font-display uppercase tracking-[0.14em] text-[14px] md:text-[15px]" style={{ color: FA_GREEN }}>
      {children}
    </h3>
  </div>
);

/* ---------- Page ---------- */
const initial = {
  fullName: '', email: '', phone: '', address: '', city: '', state: '', zip: '',
  currentOwner: '', pastOwner: '', territory: '', hearAbout: '',
  interest: '', skills: '', goals: '',
  consent: false,
};

const FranchiseApply = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [token, setToken] = useState('');
  const [hp, setHp] = useState('');
  const turnstileRef = useRef(null);
  const setTokenStable = useCallback((v) => setToken(v), []);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error('Please agree to receive communications to continue.');
      return;
    }
    if (!token) {
      toast.error('Please complete the security check before submitting.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/api/forms/franchise-application`, {
        ...form,
        [HONEYPOT_FIELD]: hp,
        turnstile_token: token,
      });
      toast.success('Application received — a member of our team will be in touch shortly.');
      setForm(initial);
      setHp('');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(typeof msg === 'string' ? msg : 'Something went wrong. Please try again.');
    } finally {
      turnstileRef.current?.reset();
      setToken('');
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-black text-white min-h-screen" data-testid="franchise-apply-page">
      <FranchiseHeader />
      <main className="bg-black franchise-body">
        {/* Back link */}
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 pt-5 md:pt-6">
          <Link
            to="/franchise"
            data-testid="back-to-franchise-link"
            className="group inline-flex items-center gap-2 font-mono text-[11px] md:text-[12px] tracking-[0.22em] uppercase text-white/85 hover:text-white transition-colors"
          >
            <ArrowLeft
              className="w-4 h-4 transition-transform duration-300 group-hover:-translate-x-0.5"
              style={{ color: FA_GREEN, filter: `drop-shadow(0 0 6px ${FA_GREEN}88)` }}
              strokeWidth={2}
            />
            Back to Franchise
          </Link>
        </div>

        {/* Two-column layout */}
        <section className="max-w-[1400px] mx-auto px-5 md:px-8 py-8 md:py-10">
          <div className="grid lg:grid-cols-[minmax(0,.42fr)_minmax(0,.58fr)] gap-6 lg:gap-8 items-stretch">
            {/* ============ LEFT PANEL — Photo background + copy ============ */}
            <aside
              data-testid="apply-left-panel"
              className="relative overflow-hidden rounded-xl min-h-[560px] lg:min-h-[unset]"
            >
              {/* Photo layer */}
              <div className="absolute inset-0">
                <img
                  src="/franchise/apply-tent.webp" srcSet="/franchise/apply-tent-400w.webp 400w, /franchise/apply-tent-800w.webp 800w, /franchise/apply-tent.webp 2000w" sizes="(max-width: 640px) 400px, (max-width: 1024px) 800px, 2000px" loading="lazy" decoding="async"
                  alt="Ionic Estate Sales branded pop-up check-out tent with two team members at an outdoor sale"
                  className="absolute inset-0 w-full h-full object-cover"
                  draggable={false}
                />
                {/* Layered dark overlays for readability */}
                <div className="absolute inset-0 bg-black/55" />
                <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/30 to-black/90" />
                <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-transparent" />
              </div>

              {/* Content */}
              <div className="relative z-10 p-6 md:p-8 lg:p-10 flex flex-col h-full">
                <img
                  src="/franchise/franchise-logo-full.webp?v=2"
                  alt="Ionic Estate Sales Franchise"
                  className="h-16 md:h-20 w-auto max-w-full self-start mb-8 md:mb-10 select-none"
                  draggable={false}
                />

                {/* Push headline block towards vertical middle-bottom */}
                <div className="mt-auto">
                  <h1 className="font-display leading-[0.95] tracking-[-0.01em] text-[38px] sm:text-[46px] md:text-[54px]">
                    <span className="block text-white">Take The First Step</span>
                    <span
                      className="block"
                      style={{ color: FA_GREEN, textShadow: `0 0 18px ${FA_GREEN}55` }}
                    >
                      Toward Ownership.
                    </span>
                  </h1>
                  <div
                    className="mt-3 h-[3px] w-32 rounded-full"
                    style={{ background: FA_GREEN, boxShadow: `0 0 14px ${FA_GREEN}` }}
                  />

                  <p className="mt-6 text-white/85 text-[14.5px] leading-[1.6] max-w-[440px]">
                    Fill out the form to apply for an Ionic Estate Sales franchise opportunity. A member of our team will reach out to learn more about you and the potential of working together.
                  </p>

                  <div className="mt-8">
                    <div
                      className="font-display uppercase tracking-[0.06em] text-[18px] md:text-[19px]"
                      style={{ color: FA_GREEN }}
                    >
                      You Bring The Drive.
                    </div>
                    <div className="font-display uppercase tracking-[0.06em] text-white text-[18px] md:text-[19px]">
                      We&rsquo;ll Provide The Rest.
                    </div>
                  </div>

                  {/* Benefit rows */}
                  <ul className="mt-6 space-y-4">
                    {BENEFITS.map((b) => {
                      const Icon = b.icon;
                      return (
                        <li key={b.t} className="flex items-start gap-4">
                          <div
                            className="shrink-0 w-14 h-14 rounded-md grid place-items-center bg-black/50"
                            style={{ border: `1.5px solid ${FA_GREEN}`, boxShadow: `0 0 18px -4px ${FA_GREEN}88` }}
                          >
                            <Icon className="w-7 h-7" style={{ color: FA_GREEN }} strokeWidth={1.4} />
                          </div>
                          <div>
                            <h4 className="font-display uppercase tracking-[0.08em] text-white text-[14.5px]">
                              {b.t}
                            </h4>
                            <p className="mt-1 text-white/75 text-[13px] leading-[1.55] max-w-[320px]">
                              {b.body}
                            </p>
                          </div>
                        </li>
                      );
                    })}
                  </ul>

                  {/* Contact block */}
                  <div
                    className="mt-8 rounded-md p-5 bg-black/50"
                    style={{ border: `1.5px solid ${FA_GREEN}88`, boxShadow: `0 0 22px -6px ${FA_GREEN}66` }}
                  >
                    <div className="flex items-start gap-3">
                      <MessageSquare className="w-6 h-6 shrink-0 mt-0.5" style={{ color: FA_GREEN }} strokeWidth={1.5} />
                      <div>
                        <div
                          className="font-display uppercase tracking-[0.14em] text-[13px]"
                          style={{ color: FA_GREEN }}
                        >
                          Questions Before You Apply?
                        </div>
                        <div className="text-white/80 text-[13px] mt-1">We&rsquo;re here to help.</div>
                        <div className="mt-3 space-y-2 text-[13.5px]">
                          <a
                            href="tel:5419414830"
                            data-testid="contact-phone"
                            className="flex items-center gap-2 text-white transition-colors"
                            onMouseEnter={(e) => { e.currentTarget.style.color = FA_GREEN; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = '#fff'; }}
                          >
                            <Phone className="w-4 h-4" style={{ color: FA_GREEN }} /> 541-941-4830
                          </a>
                          <a
                            href="mailto:ionicteam@ionicestatesales.com"
                            data-testid="contact-email"
                            className="flex items-center gap-2 text-white uppercase tracking-wide transition-colors"
                            onMouseEnter={(e) => { e.currentTarget.style.color = FA_GREEN; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = '#fff'; }}
                          >
                            <Mail className="w-4 h-4" style={{ color: FA_GREEN }} /> ionicteam@ionicestatesales.com
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </aside>

            {/* ============ RIGHT PANEL — Form ============ */}
            <form
              onSubmit={submit}
              data-testid="franchise-application-form"
              className="relative rounded-xl p-6 md:p-8 lg:p-10 bg-black"
              style={{ border: `1.5px solid ${FA_GREEN}88`, boxShadow: `0 30px 80px -30px ${FA_GREEN}44` }}
            >
              <h2 className="font-display leading-[0.95] tracking-[-0.01em] text-[34px] sm:text-[44px] md:text-[54px]">
                <span className="block text-white">Franchise</span>
                <span className="relative inline-block" style={{ color: FA_GREEN, textShadow: `0 0 14px ${FA_GREEN}55` }}>
                  Application Form
                  <span
                    className="absolute left-0 -bottom-1 w-28 h-[4px] rounded-full"
                    style={{ background: FA_GREEN, boxShadow: `0 0 14px ${FA_GREEN}` }}
                  />
                </span>
              </h2>
              <p className="mt-4 text-white/80 text-[13.5px] leading-relaxed">
                Please provide the information below.<br />
                All fields are required unless noted.
              </p>

              <SectionTitle icon={UserCircle2}>Personal Information</SectionTitle>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="fullName">Full Name</Label>
                  <Input id="fullName" type="text" required placeholder="Enter your full name" value={form.fullName} onChange={set('fullName')} />
                </div>
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <Input id="email" type="email" required placeholder="Enter your email address" value={form.email} onChange={set('email')} />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input id="phone" type="tel" required placeholder="Enter your phone number" value={form.phone} onChange={set('phone')} />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="address">Current Address</Label>
                  <Input id="address" type="text" required placeholder="Enter your street address" value={form.address} onChange={set('address')} />
                </div>
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input id="city" type="text" required placeholder="Enter your city" value={form.city} onChange={set('city')} />
                </div>
                <div>
                  <Label htmlFor="state">State</Label>
                  <Select id="state" required value={form.state} onChange={set('state')}>
                    <option value="" disabled>Select State</option>
                    {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </Select>
                </div>
                <div>
                  <Label htmlFor="zip">Zip Code</Label>
                  <Input id="zip" type="text" required inputMode="numeric" pattern="[0-9]{5}" placeholder="Enter zip code" value={form.zip} onChange={set('zip')} />
                </div>
              </div>

              <SectionTitle icon={Briefcase}>Background Information</SectionTitle>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="currentOwner">Are you currently a business owner?</Label>
                  <Select id="currentOwner" required value={form.currentOwner} onChange={set('currentOwner')}>
                    <option value="" disabled>Select an option</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="pastOwner">Have you ever owned a business before?</Label>
                  <Select id="pastOwner" required value={form.pastOwner} onChange={set('pastOwner')}>
                    <option value="" disabled>Select an option</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="territory">What is your preferred territory location?</Label>
                  <Input id="territory" type="text" placeholder="Enter preferred city, state or region" value={form.territory} onChange={set('territory')} />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="hearAbout">How did you hear about Ionic Estate Sales franchise?</Label>
                  <Select id="hearAbout" required value={form.hearAbout} onChange={set('hearAbout')}>
                    <option value="" disabled>Select an option</option>
                    {HEAR_ABOUT.map((h) => <option key={h} value={h}>{h}</option>)}
                  </Select>
                </div>
              </div>

              <SectionTitle icon={User}>About You</SectionTitle>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="interest">Why are you interested in owning an Ionic Estate Sales franchise?</Label>
                  <Textarea id="interest" required placeholder="Tell us about your goals, experience, and what excites you about this opportunity." value={form.interest} onChange={set('interest')} />
                </div>
                <div>
                  <Label htmlFor="skills">What skills or experience do you bring to the table?</Label>
                  <Textarea id="skills" required placeholder="Include any leadership, sales, customer service, or management experience." value={form.skills} onChange={set('skills')} />
                </div>
                <div>
                  <Label htmlFor="goals">What are your financial goals?</Label>
                  <Textarea id="goals" required placeholder="Tell us about your short-term and long-term financial objectives." value={form.goals} onChange={set('goals')} />
                </div>
              </div>

              <label className="mt-6 flex items-start gap-3 cursor-pointer group" htmlFor="consent">
                <input
                  id="consent"
                  data-testid="field-consent"
                  type="checkbox"
                  className="sr-only peer"
                  checked={form.consent}
                  onChange={set('consent')}
                />
                <span
                  className="mt-0.5 w-5 h-5 rounded-md grid place-items-center bg-black transition-colors shrink-0"
                  style={{ border: `1.5px solid ${form.consent ? FA_GREEN : `${FA_GREEN}77`}`, background: form.consent ? FA_GREEN : 'transparent' }}
                >
                  {form.consent && <Check className="w-4 h-4 text-black" strokeWidth={3} />}
                </span>
                <span className="text-white/85 text-[13.5px] leading-[1.5] group-hover:text-white">
                  I agree to receive communications from Ionic Estate Sales regarding my application.
                </span>
              </label>

              <HoneypotField value={hp} onChange={setHp} />
              <div className="mt-6">
                <Turnstile ref={turnstileRef} onToken={setTokenStable} theme="dark" />
              </div>

              <button
                type="submit"
                data-testid="submit-application-btn"
                disabled={submitting || !token}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 font-display uppercase tracking-[0.14em] text-[14px] md:text-[16px] text-black px-6 py-4 rounded-md transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-wait"
                style={{ background: FA_GREEN, boxShadow: `0 0 36px -6px ${FA_GREEN}` }}
              >
                {submitting ? 'Submitting…' : (<>Submit Application <ChevronRight className="w-4 h-4" strokeWidth={3} /></>)}
              </button>

              <div className="mt-4 flex items-start gap-2 text-white/70 text-[12.5px] leading-[1.5]">
                <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" style={{ color: FA_GREEN }} strokeWidth={1.75} />
                <span>Your information is secure and will only be used to contact you about this opportunity.</span>
              </div>
            </form>
          </div>
        </section>
      </main>
      <FranchiseApplyFooter />
    </div>
  );
};

export default FranchiseApply;
